/**
 * usePaymentSchedules — wraps the 6 InvoicePaymentSchedule endpoints
 * shipped in Sprint 2-3. Matches the existing useState + axios pattern
 * already used in InvoiceEditorPage (no React Query in this codebase yet).
 *
 * Endpoints (relative to api baseURL `/api/v1`):
 *   GET    /invoices/:id/payment-schedules
 *   POST   /invoices/:id/payment-schedules
 *   POST   /invoices/:id/payment-schedules/bulk
 *   PUT    /invoices/:id/payment-schedules/:scheduleId
 *   POST   /invoices/:id/payment-schedules/:scheduleId/mark-paid
 *   DELETE /invoices/:id/payment-schedules/:scheduleId
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/utils/api';

export type ModeOfPayment =
  | 'upi'
  | 'neft'
  | 'rtgs'
  | 'cheque'
  | 'cash'
  | 'bank'
  | 'wire'
  | null;

export interface PaymentSchedule {
  id: string;
  invoiceId: string;
  sequence?: number;
  dueDate: string; // ISO
  dueAmount: number | string;
  description: string | null;
  modeOfPayment: ModeOfPayment;
  paid: boolean;
  paidAmount: number | string | null;
  paidAt: string | null;
  paymentRef?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface NewMilestoneInput {
  dueDate: string;          // ISO yyyy-mm-dd
  dueAmount: number;
  description?: string;
  modeOfPayment?: Exclude<ModeOfPayment, null>;
}

export interface MarkPaidInput {
  paidAmount: number;
  modeOfPayment: Exclude<ModeOfPayment, null>;
  paidAt?: string;          // ISO
  paymentRef?: string;
}

export interface UsePaymentSchedulesResult {
  schedules: PaymentSchedule[];
  loading: boolean;
  error: string | null;
  /** Total of all dueAmount across schedules. */
  scheduledTotal: number;
  refresh: () => Promise<void>;
  createOne: (input: NewMilestoneInput) => Promise<PaymentSchedule | null>;
  createBulk: (inputs: NewMilestoneInput[]) => Promise<PaymentSchedule[]>;
  updateOne: (
    scheduleId: string,
    patch: Partial<NewMilestoneInput>
  ) => Promise<PaymentSchedule | null>;
  markPaid: (
    scheduleId: string,
    input: MarkPaidInput
  ) => Promise<PaymentSchedule | null>;
  removeOne: (scheduleId: string) => Promise<boolean>;
}

function asNumber(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
}

export function usePaymentSchedules(
  invoiceId: string | undefined | null
): UsePaymentSchedulesResult {
  const [schedules, setSchedules] = useState<PaymentSchedule[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!invoiceId) {
      setSchedules([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/invoices/${invoiceId}/payment-schedules`);
      const rows: PaymentSchedule[] =
        res.data?.data?.schedules ?? res.data?.data ?? [];
      // Sort by dueDate ascending; if equal, fall back to sequence then id.
      rows.sort((a, b) => {
        const da = new Date(a.dueDate).getTime();
        const db = new Date(b.dueDate).getTime();
        if (da !== db) return da - db;
        const sa = a.sequence ?? 0;
        const sb = b.sequence ?? 0;
        if (sa !== sb) return sa - sb;
        return a.id.localeCompare(b.id);
      });
      setSchedules(rows);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      setError(err?.response?.data?.error || 'Failed to load payment schedules');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createOne = useCallback(
    async (input: NewMilestoneInput): Promise<PaymentSchedule | null> => {
      if (!invoiceId) return null;
      setError(null);
      try {
        const res = await api.post(
          `/invoices/${invoiceId}/payment-schedules`,
          input
        );
        const created: PaymentSchedule =
          res.data?.data?.schedule ?? res.data?.data ?? null;
        await refresh();
        return created;
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to create milestone');
        return null;
      }
    },
    [invoiceId, refresh]
  );

  const createBulk = useCallback(
    async (inputs: NewMilestoneInput[]): Promise<PaymentSchedule[]> => {
      if (!invoiceId || inputs.length === 0) return [];
      setError(null);
      try {
        const res = await api.post(
          `/invoices/${invoiceId}/payment-schedules/bulk`,
          { schedules: inputs }
        );
        const created: PaymentSchedule[] =
          res.data?.data?.schedules ?? res.data?.data ?? [];
        await refresh();
        return created;
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            'Failed to create milestones (sum must equal invoice total)'
        );
        return [];
      }
    },
    [invoiceId, refresh]
  );

  const updateOne = useCallback(
    async (
      scheduleId: string,
      patch: Partial<NewMilestoneInput>
    ): Promise<PaymentSchedule | null> => {
      if (!invoiceId) return null;
      setError(null);
      try {
        const res = await api.put(
          `/invoices/${invoiceId}/payment-schedules/${scheduleId}`,
          patch
        );
        const updated: PaymentSchedule =
          res.data?.data?.schedule ?? res.data?.data ?? null;
        await refresh();
        return updated;
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to update milestone');
        return null;
      }
    },
    [invoiceId, refresh]
  );

  const markPaid = useCallback(
    async (
      scheduleId: string,
      input: MarkPaidInput
    ): Promise<PaymentSchedule | null> => {
      if (!invoiceId) return null;
      setError(null);
      try {
        const res = await api.post(
          `/invoices/${invoiceId}/payment-schedules/${scheduleId}/mark-paid`,
          input
        );
        const updated: PaymentSchedule =
          res.data?.data?.schedule ?? res.data?.data ?? null;
        await refresh();
        return updated;
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to mark milestone paid');
        return null;
      }
    },
    [invoiceId, refresh]
  );

  const removeOne = useCallback(
    async (scheduleId: string): Promise<boolean> => {
      if (!invoiceId) return false;
      setError(null);
      try {
        await api.delete(
          `/invoices/${invoiceId}/payment-schedules/${scheduleId}`
        );
        await refresh();
        return true;
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            'Failed to delete milestone (must be unpaid)'
        );
        return false;
      }
    },
    [invoiceId, refresh]
  );

  const scheduledTotal = schedules.reduce(
    (sum, s) => sum + asNumber(s.dueAmount),
    0
  );

  return {
    schedules,
    loading,
    error,
    scheduledTotal,
    refresh,
    createOne,
    createBulk,
    updateOne,
    markPaid,
    removeOne,
  };
}
